import React, { useMemo } from 'react';
import { Grid } from '@material-ui/core';
import Typography from '@material-ui/core/Typography';
import Box from '@material-ui/core/Box';
import { useForm } from 'react-hook-form';

import { Buttons } from 'shared';
import { useValidation } from 'hooks';
import { ForgotPasswordInput } from 'features/auth/ts';
import { useTranslation } from 'hooks/useTranslation';

import useAuth from 'features/auth/hooks/useAuth';
import { AuthPageLayout } from 'features/auth/components/AuthPageLayout';
import { useStyles } from 'features/auth/components/ForgotPassword/styles';
import { AuthInputField } from 'features/auth/components/AuthInputField';
import { ReturnToLoginLink } from 'features/auth/components/ReturnToLoginLink';
import { useLocation } from 'react-router-dom';

export const ForgotPassword = () => {
  const classes = useStyles();
  const { t } = useTranslation('auth');
  const { onResetPassword, isLoading } = useAuth();
  const { search } = useLocation();
  const queryParams = useMemo(() => new URLSearchParams(search), [search]);
  const { required, email } = useValidation();

  const {
    control,
    handleSubmit,
    reset,
    formState: { isSubmitting },
  } = useForm<ForgotPasswordInput>({
    mode: 'onSubmit',
    defaultValues: {
      email: decodeURIComponent(queryParams.get('email') || ''),
    },
  });

  const onSubmit = ({ email }: ForgotPasswordInput) => {
    reset({ email: '' });
    onResetPassword(email);
  };

  return (
    <AuthPageLayout title={t('forgetPassword')} additionalContentClassName={classes.forgetPasswordFormWrapper}>
      <Typography className={classes.subtitle}>{t('pleaseEnterEmail')}</Typography>
      <Grid className={classes.forgetPasswordContainer}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Box paddingTop={4} paddingBottom={2} className={classes.emailInputWrapper}>
            <AuthInputField
              required
              type="email"
              name="email"
              control={control}
              fullWidth
              label={t('emailAddress')}
              placeholder={t('yourAddressEmailPlaceholder')}
              rules={{
                validate: {
                  ...required,
                  ...email,
                },
              }}
            />
          </Box>
          <Box paddingTop={5}>
            <Buttons.Auth
              fullWidth
              disabled={isSubmitting}
              title={t('resetPassword')}
              variant="outlined"
              color="primary"
              loading={isLoading}
            />
          </Box>
        </form>

        <ReturnToLoginLink />
      </Grid>
    </AuthPageLayout>
  );
};
